import { mapActions, mapGetters } from "vuex";
import pickupTimeMixin from "@/mixins/pickup-time.mixin";
import { MODAL, ROUTE_NAMES, showDialog, ERROR_MODAL_ORDERAHEAD, IS_VENDOR_OPEN, isDevelopmentEnv } from "@/utils";

const vendorDialogMixin = {
  mixins: [pickupTimeMixin],
  methods: {
    ...mapActions({
      clearShoppingCart: "cart/clearShoppingCart",
      updateVendorById: "vendor/fetchVendorById"
    }),
    async checkVendorIsOpen() {
      try {
        await this.updateVendorById({ vendorId: this.activeVendor.id });
      } catch (error) {
        if (isDevelopmentEnv()) console.log(error);
      }

      return new Promise((resolve, reject) => {
        if (this.isVendorOpen) return resolve(true);

        this.showClosedVendorDialog();
        reject(false);
      });
    },
    showClosedVendorDialog(
      title = '<span class="red-text">Order Cannot be Completed</span>',
      errorMessage = `Sorry! The ${this.vendorName} is now closed and your order cannot be placed.`,
      buttonText = "OK",
      showCloseButton = false
    ) {
      showDialog(
        this,
        title,
        errorMessage,
        [
          {
            title: buttonText,
            handler: () => {
              this.$router.push({
                name: ROUTE_NAMES.VENDOR_PICKER
              });
              this.$modal.hide(MODAL.dialogName);
              this.clearShoppingCart();
            }
          }
        ],
        showCloseButton
      );
    },
    async checkIfTimeSlotIsValid() {
      // update vendor data
      await this.updateVendorById({ vendorId: this.activeVendor.id });

      // check if time selection is enable
      if (!this.activeVendor.pickupTimeSelectionEnabled || !this.isVenueOrderAheadAllowed) {
        return Promise.resolve(true);
      }

      let errorMessage = null;
      const availablePicksTime = this.getValidTodayPickupTimes(this.activeVendor.id);
      const isSelectedPickAvailable = availablePicksTime.includes(this.selectedPickup);

      // check if vendor is closed by role first
      if (!this.activeVendor?.status) {
        this.showClosedVendorDialog(
          `<span class="red-text">${ERROR_MODAL_ORDERAHEAD.MODAL_TITLE_CLOSED}</span>`,
          ERROR_MODAL_ORDERAHEAD.VENDOR_CLOSED,
          ERROR_MODAL_ORDERAHEAD.MODAL_BUTTON_GO_VENDORS,
          true
        );

        return Promise.reject(false);
      }

      // at the current time the store is closed
      if (!this.activeVendor[IS_VENDOR_OPEN]) {
        errorMessage = ERROR_MODAL_ORDERAHEAD.VENDOR_CLOSED_NO_TIME_SLOTS;
      }

      if (!isSelectedPickAvailable || errorMessage) {
        // No slot but at the current time the store is still open
        if (!availablePicksTime.length && this.activeVendor[IS_VENDOR_OPEN]) {
          errorMessage = ERROR_MODAL_ORDERAHEAD.VENDOR_OPEN_NO_TIME_SLOTS;
        }

        if (errorMessage) {
          showDialog(
            this,
            `<span class="red-text">${ERROR_MODAL_ORDERAHEAD.MODAL_TITLE}</span>`,
            errorMessage,
            [
              {
                title: ERROR_MODAL_ORDERAHEAD.MODAL_BUTTON_GO_MENU,
                handler: () => {
                  this.$router.push({
                    name: ROUTE_NAMES.MENU,
                    params: { vendor: this.activeVendor.id }
                  });
                  this.$modal.hide(MODAL.dialogName);
                }
              }
            ],
            true
          );

          return Promise.reject(false);
        }

        errorMessage =
          availablePicksTime.length === 1
            ? ERROR_MODAL_ORDERAHEAD.ONLY_ONE_SLOT_AVAILABLE
            : ERROR_MODAL_ORDERAHEAD.TWO_OR_MORE_SLOTS_AVAILABLE;

        this.showPickupTimeOptions(
          this.activeVendor.id,
          {
            title: ERROR_MODAL_ORDERAHEAD.MODAL_TITLE,
            buttonText: ERROR_MODAL_ORDERAHEAD.MODAL_BUTTON_WITH_SLOTS,
            errorMessage
          },
          false
        );

        return Promise.reject(false);
      }

      return Promise.resolve(true);
    }
  },
  computed: {
    ...mapGetters({
      isVendorOpen: "vendor/isActiveVendorOpen",
      vendorsList: "vendor/getVendorsList",
      activeVendor: "vendor/getActiveVendor",
      getValidTodayPickupTimes: "vendor/getValidPickupTimesForTodayByVendorId",
      vendorAliasSingular: "venue/getVendorAliasSingular"
    }),
    vendorName() {
      return this.vendorAliasSingular || "vendor";
    }
  }
};

export default vendorDialogMixin;
